import { AuthenticatedApp } from './components/AuthenticatedApp';
import { WalletProvider } from './context/wallet';
import { CostConfirmDialog, InsufficientFundsDialog } from './components/Wallet';
import { TopUpReturnHandler } from './components/Wallet/TopUpReturnHandler';
import { UnauthenticatedApp } from './components/UnauthenticatedApp';
import { UserProfile } from './components/UserProfile';
import { Footer } from './components/Footer';
import { resolveLegalPage } from './components/Legal';
import { useAuth } from './hooks/useAuth';
import { GetCurrentUser } from './Client/ManagementServiceClient';
import { useState, useEffect, useCallback } from 'react';
import { useModalAccessibility } from './hooks/useModalAccessibility';
import './App.css';

const IMPERSONATE_USER_STORAGE_KEY = 'vixo_impersonate_user_id';
const IMPERSONATE_EMAIL_STORAGE_KEY = 'vixo_impersonate_user_email';
const IMPERSONATION_CHANGED_EVENT = 'vixo-impersonation-changed';

const readImpersonationSession = () => {
    const userId = String(localStorage.getItem(IMPERSONATE_USER_STORAGE_KEY) || '').trim();
    const email = String(localStorage.getItem(IMPERSONATE_EMAIL_STORAGE_KEY) || '').trim().toLowerCase();
    return {
        userId,
        email,
        active: Boolean(userId || email)
    };
};

function App() {
    // Focus, Tab, Esc and focus-return for every modal, signed in or out.
    useModalAccessibility();
    const { user, getIdToken, logout } = useAuth();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [needsProfile, setNeedsProfile] = useState(false);
    const [impersonationSession, setImpersonationSession] = useState(readImpersonationSession);
    const impersonationSessionActive = impersonationSession.active;
    const isAdmin = String(userProfile?.UserRole || '').toLowerCase() === 'admin';
    // Non-prod (staging) deployments are restricted to admin-role users. Prod
    // sets no REACT_APP_ENV, so it is never gated; the backend enforces the same
    // rule authoritatively by Environment.
    const isNonProd = Boolean(process.env.REACT_APP_ENV) && process.env.REACT_APP_ENV !== 'prod';

    useEffect(() => {
        const syncImpersonation = () => {
            setImpersonationSession(readImpersonationSession());
        };

        window.addEventListener('storage', syncImpersonation);
        window.addEventListener(IMPERSONATION_CHANGED_EVENT, syncImpersonation);
        return () => {
            window.removeEventListener('storage', syncImpersonation);
            window.removeEventListener(IMPERSONATION_CHANGED_EVENT, syncImpersonation);
        };
    }, []);

    useEffect(() => {
        const originalFetch = window.fetch;

        window.fetch = async (input, init = {}) => {
            const impersonateUserId = localStorage.getItem(IMPERSONATE_USER_STORAGE_KEY);
            const impersonateUserEmail = localStorage.getItem(IMPERSONATE_EMAIL_STORAGE_KEY);
            const uidValue = String(impersonateUserId || '').trim();
            const emailValue = String(impersonateUserEmail || '').trim().toLowerCase();

            if (!uidValue && !emailValue) {
                return originalFetch(input, init);
            }

            const nextInit = { ...init };
            const headers = new Headers(nextInit.headers || {});
            if (uidValue) {
                headers.set('X-Impersonate-User-Id', uidValue);
            }
            if (emailValue) {
                headers.set('X-Impersonate-User-Email', emailValue);
            }
            nextInit.headers = headers;

            return originalFetch(input, nextInit);
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, []);

    useEffect(() => {
        const checkUserProfile = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            if (impersonationSessionActive) {
                setNeedsProfile(false);
                setLoading(false);
                return;
            }

            try {
                const idToken = await getIdToken();
                let result = await GetCurrentUser(idToken);

                if (result.status === 401) {
                    // Token may have just expired; refresh once and retry
                    const freshToken = await getIdToken(true);
                    result = await GetCurrentUser(freshToken);
                }

                if (result.status === 200) {
                    // User profile exists
                    setUserProfile(result.myJson);
                    if (result.myJson.HasAcceptedTerms && result.myJson.stripe_payment_method_added) {
                        // User accepted terms AND has payment method set up
                        setNeedsProfile(false);
                    } else {
                        // User needs to complete T&Cs and/or Stripe setup
                        setNeedsProfile(true);
                    }
                } else if (result.status === 404) {
                    // User profile doesn't exist
                    setUserProfile(null);
                    setNeedsProfile(true);
                } else {
                    // Transient backend failure: keep the user in the app
                    // instead of bouncing them to onboarding.
                    console.error('Error checking user profile:', result);
                }
            } catch (err) {
                // Network/auth hiccups should not look like a logout
                console.error('Error fetching user profile:', err);
            } finally {
                setLoading(false);
            }
        };

        checkUserProfile();
    }, [user, impersonationSessionActive, getIdToken]);

    // Stable identity so UserProfile's effects don't re-run on every App render.
    // Store the returned profile so the app has the fresh payment/terms flags.
    const handleProfileComplete = useCallback((updatedProfile) => {
        if (updatedProfile) {
            setUserProfile(updatedProfile);
        }
        setNeedsProfile(false);
    }, []);

    const stopImpersonation = () => {
        localStorage.removeItem(IMPERSONATE_EMAIL_STORAGE_KEY);
        localStorage.removeItem(IMPERSONATE_USER_STORAGE_KEY);
        setImpersonationSession(readImpersonationSession());
        window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
    };

    console.log(user);

    // Terms and Privacy must render for anyone, signed in or not: the Google
    // OAuth and Meta app reviews fetch these URLs anonymously, and a login wall
    // or a loading spinner reads as a missing policy. Checked ahead of the auth
    // and profile gates below, but after the hooks above so the hook order
    // stays stable.
    const legalPage = resolveLegalPage(window.location.pathname);
    if (legalPage) {
        return (
            <div className="app-container">
                {legalPage}
                <Footer />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="app-container">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <p>Loading...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="app-container">
                <UnauthenticatedApp />
                <Footer />
            </div>
        );
    }

    if (needsProfile && !impersonationSessionActive) {
        return (
            <div className="app-container">
                <UserProfile
                    onProfileComplete={handleProfileComplete}
                    existingProfile={userProfile}
                />
                <Footer />
            </div>
        );
    }

    // Non-prod (staging) is admin-only. Non-admins get a restricted screen
    // instead of the app; the backend enforces the same rule on every request.
    if (isNonProd && !isAdmin) {
        return (
            <div className="app-container app-shell-fixed">
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    padding: '24px',
                    gap: '10px'
                }}>
                    <h2 style={{ margin: 0 }}>Staging is restricted</h2>
                    <p style={{ color: 'var(--sb-ink-muted)', maxWidth: 440, margin: 0 }}>
                        This non-production environment is available to administrators only.
                        Please use the production app, or contact an admin if you need access.
                    </p>
                    <button className="btn btn-secondary" style={{ marginTop: '8px' }} onClick={logout}>
                        Sign out
                    </button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="app-container app-shell-fixed">
            {impersonationSessionActive && (
                <div style={{
                    margin: '8px 12px 0 12px',
                    padding: '10px 12px',
                    borderRadius: 'var(--sb-corner)',
                    border: '1px solid var(--sb-caution)',
                    backgroundColor: 'var(--sb-caution-wash)',
                    color: 'var(--sb-caution)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span>
                        <strong>Impersonation active:</strong> {impersonationSession.email || 'unknown email'}
                        {impersonationSession.userId ? ` (${impersonationSession.userId})` : ''}
                    </span>
                    <button className="btn btn-sm btn-secondary" onClick={stopImpersonation}>Stop</button>
                </div>
            )}
            {/* The wallet wraps the authenticated app so the nav pill and any
                AI action share one balance, and so the out-of-funds dialog is
                mounted once rather than per feature. */}
            <WalletProvider>
                <AuthenticatedApp userProfile={userProfile} isAdmin={isAdmin} />
                <InsufficientFundsDialog />
                <CostConfirmDialog />
                <TopUpReturnHandler />
            </WalletProvider>
            <Footer />
        </div>
    );
}

export default App;